/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import axios from 'axios'
import { setAuthorizationConfig } from '@/utils/services'
import { samApiUrls } from '@/configs/api-urls/sam.api-urls'
import { setOffset } from '@/utils/pagination'
import { setUrlWithParams } from '@/utils/url'
import { getValidSearchParams, handleThrowError } from '@/utils/fetch'

const { base: apiUrlBase, aiMetadata, revenueBreakdown } = samApiUrls

export const createSam = async (data, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .post(apiUrlBase, data, config)
    .then(res => res.data)
    .catch(err => handleThrowError('createSam', err))
}

export const updateSam = async (samId, data, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .put(`${apiUrlBase}/${samId}`, data, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('updateSam', err) })
}

export const deleteSam = async (samId, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .delete(`${apiUrlBase}/${samId}`, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('deleteSam', err) })
}

export const getSams = async (
  { searchParams },
  getAccessToken
) => {
  const limit = parseInt(searchParams.limit) || 10

  const pagination = {
    limit,
    offset: setOffset(searchParams.page, limit)
  }

  const params = { ...getValidSearchParams(searchParams) }

  if (params.page) {
    delete params.page
  }
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(setUrlWithParams(apiUrlBase, { ...params, ...pagination }), config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getSams', err) })
}

export const getSamsAsCsv = async (
  getAccessToken
) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(`${apiUrlBase}/reports/csv`, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getSamsAsCsv', err) })
}

export const getSam = async (
  { samId },
  getAccessToken
) => {
  let config = {}

  if (getAccessToken) {
    const accessToken = await getAccessToken()

    config = {
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      }
    }
  }

  return axios
    .get(`${apiUrlBase}/${samId}`, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getSams', err) })
}

export const listSam = async (samId, data, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .post(`${apiUrlBase}/${samId}/listing`, data, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('listSam', err) })
}

export const unlistSam = async (samId, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .put(`${apiUrlBase}/${samId}/unlist`, undefined, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('unlistSam', err) })
}

export const createAiMetadata = async (samId, data, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .post(`${apiUrlBase}/${samId}/ai-metadata`, data, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('createAiMetadata', err) })
}

export const getCompaniesWithAiUsageLicense = async (
  { orderField, orderType, searchParams },
  getAccessToken
) => {
  const limit = parseInt(searchParams.limit) || 10

  const pagination = {
    limit,
    offset: setOffset(searchParams.page, limit)
  }

  const params = { ...searchParams }

  const config = await setAuthorizationConfig(getAccessToken)
  const url = `${apiUrlBase}/${samApiUrls.manage}`

  return axios
    .get(
      setUrlWithParams(url, {
        ...params,
        ...pagination,
        orderField,
        orderType
      }),
      config
    )
    .then((res) => res.data)
    .catch((err) => {
      handleThrowError('getCompaniesWithAiUsageLicense', err)
    })
}

export const getCompanyWithAiUsageLicense = async (
  { manageId },
  getAccessToken
) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const url = `${apiUrlBase}/${samApiUrls.manage}/${manageId}`

  return axios
    .get(url, config)
    .then((res) => res.data)
    .catch((err) => {
      handleThrowError('getCompanyWithAiUsageLicense', err)
    })
}

export const getAssetsByCompany = async (
  { manageId },
  getAccessToken
) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const url = `${apiUrlBase}/${samApiUrls.manage}/${manageId}/assets`

  return axios
    .get(url, config)
    .then((res) => res.data)
    .catch((err) => {
      handleThrowError('getAssetsByCompany', err)
    })
}

export const getAIMetadataBySamId = async (
  { samId },
  getAccessToken
) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(`${apiUrlBase}/${samId}/${aiMetadata}`, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAIMetadataBySamId', err) })
}

export const getRevenueBreakdownBySamId = async (
  { id },
  getAccessToken
) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(`${apiUrlBase}/${id}/${revenueBreakdown}`, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getRevenueBreakdownByAssetId', err) })
}
